import React, { useState, useEffect } from 'react';
import { 
  Settings, QrCode, Receipt, Send, AlertTriangle, 
  Save, Loader2, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPOSSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    upi_vpa: '',
    merchant_name: '',
    default_gst_rate: 0,
    enable_gst: true,
    receipt_header: '',
    receipt_footer: '',
    low_stock_threshold: 10,
    interakt_api_key: '',
    interakt_template_name: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSettings({
        upi_vpa: data.upi_vpa || 'nammahut@upi',
        merchant_name: data.merchant_name || 'NammaHut',
        default_gst_rate: data.default_gst_rate || 0,
        enable_gst: data.enable_gst !== false,
        receipt_header: data.receipt_header || '',
        receipt_footer: data.receipt_footer || '',
        low_stock_threshold: data.low_stock_threshold || 10,
        interakt_api_key: data.interakt_api_key || '',
        interakt_template_name: data.interakt_template_name || ''
      });
    } catch (error) {
      toast.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-pos-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">POS Settings</h1>
        <p className="text-gray-500">Configure your Point of Sale system</p>
      </div>

      {/* UPI Settings */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <QrCode className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">UPI Payment Settings</h3>
            <p className="text-sm text-gray-500">Configure UPI QR code generation</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              UPI VPA (Virtual Payment Address)
            </label>
            <Input
              value={settings.upi_vpa}
              onChange={(e) => setSettings({...settings, upi_vpa: e.target.value})}
              placeholder="yourstore@upi"
            />
            <p className="text-xs text-gray-500 mt-1">e.g., nammahut@ybl, store@paytm</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merchant Name
            </label>
            <Input
              value={settings.merchant_name}
              onChange={(e) => setSettings({...settings, merchant_name: e.target.value})}
              placeholder="Your Store Name"
            />
            <p className="text-xs text-gray-500 mt-1">Displayed on UPI payment screen</p>
          </div>
        </div>
      </div>

      {/* GST Settings */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Receipt className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Tax Settings</h3>
            <p className="text-sm text-gray-500">Configure GST for POS billing</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enable_gst}
              onChange={(e) => setSettings({...settings, enable_gst: e.target.checked})}
              className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="font-medium">Enable GST on POS billing</span>
          </label>
          
          {settings.enable_gst && (
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default GST Rate (%)
              </label>
              <Input
                type="number"
                min="0"
                max="28"
                value={settings.default_gst_rate}
                onChange={(e) => setSettings({...settings, default_gst_rate: parseFloat(e.target.value) || 0})}
                placeholder="18"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used when product doesn't have specific GST rate. Common rates: 5%, 12%, 18%
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            You can set product-specific GST rates when adding products. The rate set here is used as a fallback.
          </p>
        </div>
      </div>

      {/* WhatsApp Settings */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Send className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">WhatsApp Bill Integration</h3>
            <p className="text-sm text-gray-500">Send bills via Interakt WhatsApp API</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interakt API Key
            </label>
            <Input
              type="password"
              value={settings.interakt_api_key}
              onChange={(e) => setSettings({...settings, interakt_api_key: e.target.value})}
              placeholder="Enter your Interakt API key"
            />
            <p className="text-xs text-gray-500 mt-1">
              Get your API key from <a href="https://app.interakt.ai" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Interakt Dashboard</a>
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Template Name (optional)
            </label>
            <Input
              value={settings.interakt_template_name}
              onChange={(e) => setSettings({...settings, interakt_template_name: e.target.value})}
              placeholder="bill_receipt"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use default text message format
            </p>
          </div>
        </div>
      </div>

      {/* Receipt Settings */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Receipt className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Receipt Customization</h3>
            <p className="text-sm text-gray-500">Customize bill/receipt appearance</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Receipt Header
            </label>
            <Input
              value={settings.receipt_header}
              onChange={(e) => setSettings({...settings, receipt_header: e.target.value})}
              placeholder="NammaHut - Traditional Marketplace"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Receipt Footer
            </label>
            <Input
              value={settings.receipt_footer}
              onChange={(e) => setSettings({...settings, receipt_footer: e.target.value})}
              placeholder="Thank you for shopping with us!"
            />
          </div>
        </div>
      </div>

      {/* Stock Alert Settings */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Inventory Alerts</h3>
            <p className="text-sm text-gray-500">Configure low stock notifications</p>
          </div>
        </div>

        <div className="max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Low Stock Threshold
          </label>
          <Input
            type="number"
            min="1"
            value={settings.low_stock_threshold}
            onChange={(e) => setSettings({...settings, low_stock_threshold: parseInt(e.target.value) || 10})}
          />
          <p className="text-xs text-gray-500 mt-1">
            Products with stock below this will show in alerts
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 px-8"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
